import React from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Printer, FileText, ChevronRight } from "lucide-react";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const CANONICAL_TITLES = {
  terms: { en: "Terms of Service — LoftPress", tr: "Kullanım Koşulları — LoftPress" },
  privacy: { en: "Privacy Policy — LoftPress", tr: "Gizlilik Politikası — LoftPress" },
  aup: { en: "Acceptable Use Policy — LoftPress", tr: "Kabul Edilebilir Kullanım Politikası — LoftPress" },
  contact: { en: "Contact — LoftPress", tr: "İletişim — LoftPress" },
};
const CANONICAL_DESC = {
  terms: { en: "Terms of Service for the LoftPress API platform operated by LOFT PRESS LTD.", tr: "LOFT PRESS LTD tarafından işletilen LoftPress API platformunun Kullanım Koşulları." },
  privacy: { en: "How LOFT PRESS LTD processes personal data under UK GDPR, EU GDPR and Turkish KVKK.", tr: "LOFT PRESS LTD’in UK GDPR, AB GDPR ve KVKK kapsamında kişisel verileri nasıl işlediği." },
  aup: { en: "Acceptable Use Policy for the LoftPress API platform.", tr: "LoftPress API platformu için Kabul Edilebilir Kullanım Politikası." },
  contact: { en: "How to contact LOFT PRESS LTD for support, billing, privacy or legal matters.", tr: "LOFT PRESS LTD ile destek, faturalama, gizlilik veya hukuki konular için nasıl iletişime geçileceği." },
};

function slugify(str) {
  return (str || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
}

function extractHeadings(md) {
  if (!md) return [];
  const out = [];
  const lines = md.split(/\r?\n/);
  for (const line of lines) {
    const m = /^(#{2,3})\s+(.+?)\s*$/.exec(line);
    if (!m) continue;
    const level = m[1].length;
    const text = m[2].replace(/\*\*/g, "");
    out.push({ level, text, id: slugify(text) });
  }
  return out;
}

const MD_COMPONENTS = {
  h1: ({ node, children, ...props }) => (
    <h1 className="font-display text-3xl sm:text-4xl text-white/95 mt-2 mb-3" {...props}>{children}</h1>
  ),
  h2: ({ node, children, ...props }) => {
    const text = React.Children.toArray(children).join("");
    return (
      <h2 id={slugify(text)} className="font-display text-xl sm:text-2xl text-white mt-10 mb-3 scroll-mt-24" {...props}>{children}</h2>
    );
  },
  h3: ({ node, children, ...props }) => {
    const text = React.Children.toArray(children).join("");
    return (
      <h3 id={slugify(text)} className="font-display text-lg text-white/90 mt-6 mb-2 scroll-mt-24" {...props}>{children}</h3>
    );
  },
  p: ({ node, children, ...props }) => (
    <p className="text-white/70 leading-relaxed my-3" {...props}>{children}</p>
  ),
  ul: ({ node, children, ...props }) => (
    <ul className="list-disc pl-6 my-3 space-y-1 text-white/70" {...props}>{children}</ul>
  ),
  ol: ({ node, children, ...props }) => (
    <ol className="list-decimal pl-6 my-3 space-y-1 text-white/70" {...props}>{children}</ol>
  ),
  a: ({ node, href, children, ...props }) => (
    <a href={href} className="text-[#d4af37] underline underline-offset-2 hover:text-[#f0c956]" target={href?.startsWith("http") ? "_blank" : undefined} rel={href?.startsWith("http") ? "noreferrer noopener" : undefined} {...props}>{children}</a>
  ),
  strong: ({ node, children, ...props }) => (
    <strong className="text-white/90" {...props}>{children}</strong>
  ),
  blockquote: ({ node, children, ...props }) => (
    <blockquote className="border-l-2 border-[#d4af37]/60 pl-4 italic text-white/70 my-4" {...props}>{children}</blockquote>
  ),
  code: ({ inline, children, ...props }) => inline
    ? <code className="px-1.5 py-0.5 rounded bg-white/5 text-[#d4af37] text-[0.85em]" {...props}>{children}</code>
    : <code {...props}>{children}</code>,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4">
      <table className="min-w-full text-sm border border-white/10 rounded-md overflow-hidden">{children}</table>
    </div>
  ),
  thead: ({ children }) => (<thead className="bg-white/5 text-white/80">{children}</thead>),
  th: ({ children }) => (<th className="text-left px-3 py-2 border-b border-white/10 font-medium">{children}</th>),
  td: ({ children }) => (<td className="px-3 py-2 border-b border-white/5 text-white/70 align-top">{children}</td>),
  hr: () => (<hr className="border-white/10 my-6" />),
};


function ContactForm({ lang }) {
  const { t } = useLang();
  const [form, setForm] = React.useState({
    name: "", email: "", subject_category: "sales", order_id: "", message: "",
    privacy_ack: false, honeypot: "",
  });
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/support/contact", { ...form, language: lang });
      setDone(true);
      setForm({ name: "", email: "", subject_category: "sales", order_id: "", message: "", privacy_ack: false, honeypot: "" });
      toast.success(t("contact.form.success"));
    } catch (err) {
      const msg = err?.response?.data?.detail || t("contact.form.error");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="lp-card p-6 text-sm text-white/80" data-testid="contact-form-success">
        {t("contact.form.thanks")}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="lp-card p-6 space-y-4" data-testid="contact-form">
      {/* honeypot */}
      <input type="text" tabIndex="-1" autoComplete="off" value={form.honeypot} onChange={(e) => setForm({ ...form, honeypot: e.target.value })} className="hidden" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">{t("contact.form.name")}</label>
          <input required minLength={2} maxLength={120} className="lp-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">{t("contact.form.email")}</label>
          <input required type="email" maxLength={200} className="lp-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">{t("contact.form.category")}</label>
          <select className="lp-input" value={form.subject_category} onChange={(e) => setForm({ ...form, subject_category: e.target.value })} data-testid="contact-category">
            <option value="sales">{t("contact.cat.sales")}</option>
            <option value="technical_support">{t("contact.cat.technical")}</option>
            <option value="billing_refund">{t("contact.cat.billing")}</option>
            <option value="privacy_data_request">{t("contact.cat.privacy")}</option>
            <option value="legal_notice">{t("contact.cat.legal")}</option>
            <option value="security_report">{t("contact.cat.security")}</option>
            <option value="other">{t("contact.cat.other")}</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">{t("contact.form.order")}</label>
          <input maxLength={64} className="lp-input" placeholder={t("contact.form.order_ph")} value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} data-testid="contact-order" />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">{t("contact.form.message")}</label>
        <textarea required minLength={20} maxLength={4000} rows={6} className="lp-input font-mono text-sm" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
      </div>

      <label className="flex items-start gap-2 text-sm text-white/70">
        <input required type="checkbox" checked={form.privacy_ack} onChange={(e) => setForm({ ...form, privacy_ack: e.target.checked })} data-testid="contact-privacy-ack" />
        <span>
          {t("contact.form.ack.pre")}{" "}
          <Link to="/legal/privacy" className="text-[#d4af37] underline">{t("contact.form.ack.privacy")}</Link>.
        </span>
      </label>

      <button type="submit" disabled={busy} className="lp-btn-primary" data-testid="contact-submit">
        {busy ? "…" : t("contact.form.submit")}
      </button>

      <p className="text-xs text-white/40">
        {t("contact.form.note")}
      </p>
    </form>
  );
}


export default function LegalPage() {
  const { kind } = useParams();
  const { t, lang, formatDate } = useLang();
  const [doc, setDoc] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);
  const [company, setCompany] = React.useState(null);

  React.useEffect(() => {
    setDoc(null); setNotFound(false);
    api.get(`/api/legal/${kind}`).then((r) => setDoc(r.data)).catch(() => setNotFound(true));
    api.get("/api/company/public").then((r) => setCompany(r.data)).catch(() => {});
  }, [kind]);

  React.useEffect(() => {
    const canonical = CANONICAL_TITLES[kind]?.[lang];
    if (canonical) document.title = canonical;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && CANONICAL_DESC[kind]?.[lang]) {
      meta.setAttribute("content", CANONICAL_DESC[kind][lang]);
    }
  }, [kind, lang]);

  const title = doc ? (lang === "tr" ? doc.title_tr : doc.title_en) : "";
  const body = doc ? (lang === "tr" ? doc.body_tr : doc.body_en) : "";
  const headings = React.useMemo(() => extractHeadings(body), [body]);

  const isContact = kind === "contact";

  return (
    <div>
      <Header />
      <Seo title={doc?doc.title:"Legal"} description={doc?doc.title+" — LoftPress":""} path={`/legal/${kind||""}`} />
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16 print:py-0">
        <Link to="/" className="text-sm text-[#d4af37] mb-6 inline-block print:hidden">← {t("legal.home")}</Link>

        {/* Header row */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-[#d4af37] font-mono mb-2">{t("legal.eyebrow")}</div>
            <h1 className="font-display text-3xl sm:text-4xl text-white/95 break-words">{title || (kind || "").replace(/-/g, " ")}</h1>
            {doc && (
              <div className="text-xs text-white/40 mt-2 font-mono">
                {t("legal.version")} {doc.version} · {t("legal.updated")}: {doc.updated_at ? formatDate(doc.updated_at) : "—"}
              </div>
            )}
          </div>
          {doc && !isContact && (
            <div className="flex gap-2 print:hidden">
              <button onClick={() => window.print()} className="lp-btn-secondary text-xs" data-testid="legal-print"><Printer className="w-3.5 h-3.5" /> {t("legal.print")}</button>
            </div>
          )}
        </div>

        {notFound ? (
          <div className="lp-card p-8 mt-8 text-white/60">{t("legal.pending")}</div>
        ) : !doc ? (
          <div className="lp-card p-8 mt-8 h-40 lp-shimmer" />
        ) : (
          <div className="mt-8 grid lg:grid-cols-[220px_minmax(0,1fr)] gap-8">
            {/* TOC */}
            {headings.length > 0 && (
              <aside className="hidden lg:block print:hidden">
                <div className="sticky top-24">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">{t("legal.toc")}</div>
                  <nav className="space-y-1 text-sm">
                    {headings.map((h) => (
                      <a key={h.id + h.text} href={`#${h.id}`}
                         className={`block text-white/60 hover:text-white transition-colors ${h.level === 3 ? "pl-3" : ""}`}
                         data-testid={`toc-${h.id}`}>
                        <span className="inline-flex items-center gap-1"><ChevronRight className="w-3 h-3 text-[#d4af37]/60" /> {h.text.replace(/^\d+\.\s+/, "")}</span>
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>
            )}

            <div className="min-w-0">
              <article className="lp-card p-5 sm:p-8 text-white/75" data-testid="legal-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                  {/* Skip the leading H1 because we already render it in the page header */}
                  {body.replace(/^#\s+.+\n/, "")}
                </ReactMarkdown>
              </article>

              {isContact && (
                <div className="mt-6" data-testid="contact-form-block">
                  <h2 className="font-display text-2xl text-white mt-6 mb-3">{t("contact.form.title")}</h2>
                  <ContactForm lang={lang} />
                </div>
              )}
            </div>
          </div>
        )}

        {company && (
          <div className="lp-glass p-5 mt-8 text-sm text-white/60 space-y-1 print:mt-6" data-testid="legal-company-block">
            {company.legal_name && <div className="text-white/85 font-medium" data-testid="company-legal-name">{company.legal_name}</div>}
            {doc?.slug === "terms" || doc?.slug === "privacy" || doc?.slug === "contact" ? (
              <div data-testid="company-number">Companies House no. 16676296 — England & Wales</div>
            ) : null}
            {company.registered_address && <div data-testid="company-address">{company.registered_address}</div>}
            {company.website && <div><a href={company.website} className="text-[#d4af37] hover:underline">{company.website}</a></div>}
            {company.support_email && <div data-testid="company-support">{t("legal.support")}: <a className="text-[#d4af37] hover:underline" href={`mailto:${company.support_email}`}>{company.support_email}</a></div>}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
